import React, { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LanguageProvider, useLang } from "./lib/i18n";
import { api } from "./lib/api";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingContact from "./components/FloatingContact";
import Home from "./pages/Home";
import ServicePage from "./pages/ServicePage";
import Packages from "./pages/Packages";
import Booking from "./pages/Booking";
import Quote from "./pages/Quote";
import Admin from "./pages/Admin";

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white px-6">
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-forest-900">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground text-sm">Please refresh the page.</p>
            <button onClick={() => window.location.reload()} className="mt-6 rounded-full bg-forest-700 px-6 py-3 text-white font-semibold">Refresh</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const timer = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const LenisSetup = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const loop = (time) => { lenis.raf(time); rafId = requestAnimationFrame(loop); };
    rafId = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });
  return (
    <>
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services/:slug" element={<ServicePage />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdmin && <Footer services={services} />}
      {!isAdmin && <FloatingContact />}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <BrowserRouter>
          <LenisSetup />
          <ScrollToTop />
          <Shell />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;